import React, { useEffect, useState, Fragment } from 'react';
import { filter, find } from 'ramda';
import Button from '~~elements/Button';
import Modal from '~~elements/Modal';
import Multilingual from '~~utils/Multilingual';
import { SchedulePanelDiv, ModalFilterDiv, ListModeDiv } from '../styled';
import moment from 'moment';

const ScheduleGroup = ({
  tournament,
  games,
  groups,
  gameLocations,
  handleGameFilterOption,
  handleGameFilterDateRange,
  selectedLocations,
  selectedDateRange,
  setGameListCollapse,
  gameListCollapse,
  setSelectedGame,
  handleTryCreateGame,
}) => {
  const [groupedGames, setGroupedGames] = useState({});

  const [isGameFilterOpen, setGameFilterOpen] = useState(false);

  useEffect(() => {
    const getMatchedGames = (groupName) => {
      return filter((g) => g.group === groupName, games);
    };

    const grouping = {
      NONE: getMatchedGames('NONE'),
    };

    groups.forEach((group) => {
      grouping[group.name] = getMatchedGames(group.name);
      return;
    });

    setGroupedGames(grouping);
  }, [games, groups]);

  const handleGameFilterClick = () => {
    setGameFilterOpen(!isGameFilterOpen);
  };

  const handleGroupCollapse = () => {
    setGameListCollapse(!gameListCollapse);
  };

  const getGamesInGroup = (groupName) => {
    const matchFilter = (game) => {
      if (selectedLocations.length > 0 && !selectedLocations.includes(game.info.location_uniqid)) {
        return false;
      }

      const startDate = selectedDateRange[0];
      const endDate = selectedDateRange[1];
      const gameTime = new Date(game.info.time.scheduled_start_at);
      if (startDate && moment(gameTime).isBefore(startDate)) {
        return false;
      }
      if (endDate && moment(endDate).isBefore(gameTime)) {
        return false;
      }
      return true;
    };

    const getTeamClassName = (game, side) => {
      if (game.info.status !== 'FINISHED') {
        return '';
      }

      return game.info.winner_side === side ? 'win' : 'lose';
    };

    const handleGameClick = (gameUniqid) => () => {
      setSelectedGame(gameUniqid);
    };

    return filter(matchFilter, groupedGames[groupName])
      .reverse()
      .map((game) => (
        <div
          key={game.uniqid}
          className={`list-mode-item--game ${game.info.status === 'FINISHED' ? 'finished' : ''}`}
          onClick={handleGameClick(game.uniqid)}
        >
          <div>
            <h4>{`G${game.seq}`}</h4>
            <label>&nbsp;</label>
            <label>{moment(game.info.time.scheduled_start_at).format('MM/DD HH:mm')}</label>
          </div>
          <div>
            <label className={getTeamClassName(game, 'AWAY')}>
              {!game.away.team ? Multilingual(`GAME.TEAM_NONE`) : game.away.team}
            </label>
            <label>&nbsp;/&nbsp;</label>
            <label className={getTeamClassName(game, 'HOME')}>
              {!game.home.team ? Multilingual(`GAME.TEAM_NONE`) : game.home.team}
            </label>
          </div>
          <label>{game.info.location}</label>
        </div>
      ));
  };

  return (
    <Fragment>
      <SchedulePanelDiv>
        <div className="schedule-panel schedule-panel--start">
          <Button width="unset" color="transparentWithBorder" nowrap onClick={handleGameFilterClick}>
            {Multilingual(`TOURNAMENT.FILTER`)}
          </Button>
          <label>
            {`${Multilingual(`TOURNAMENT.GAME_LOCATION`)}：`}
            {selectedLocations.length === 0
              ? Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)
              : selectedLocations
                  .map((locationUniqid) => {
                    const location = find((l) => l.uniqid === locationUniqid, gameLocations);
                    return !location ? '' : location.name;
                  })
                  .join(', ')}
          </label>
          <label>
            {`${Multilingual(`TOURNAMENT.DATE_RANGE`)}：`}
            {!selectedDateRange[0] && !selectedDateRange[1]
              ? Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)
              : `${!selectedDateRange[0] ? '' : moment(selectedDateRange[0]).format('YYYY/MM/DD')} - ${
                  !selectedDateRange[1] ? '' : moment(selectedDateRange[1]).format('YYYY/MM/DD')
                }`}
          </label>
        </div>
        <div className="schedule-panel schedule-panel--end">
          <Button width="unset" color="transparentWithBorder" nowrap onClick={handleGroupCollapse}>{`${
            gameListCollapse ? 'V' : 'X'
          } ${Multilingual(`CALENDAR.COLLAPSE`)}`}</Button>
        </div>
      </SchedulePanelDiv>
      <ListModeDiv>
        <div className="list-mode">
          {Object.keys(groupedGames).map((groupName) => (
            <div key={groupName} className={`list-mode-item ${gameListCollapse ? 'collapse' : ''}`}>
              <h3 className="list-mode-item--name">
                {groupName === 'NONE' ? Multilingual(`TOURNAMENT.GAME_GROUP_NONE`) : groupName}
                <Button
                  width="unset"
                  color="transparentWithBorder"
                  mini
                  onClick={handleTryCreateGame(null, groupName, '')}
                >
                  {Multilingual(`TOURNAMENT.CREATE_GAME`)}
                </Button>
              </h3>
              <div className={`list-mode-item--games`}>{getGamesInGroup(groupName)}</div>
            </div>
          ))}
        </div>
      </ListModeDiv>
      <Modal.Modal size="lg" isOpen={isGameFilterOpen} outterClickBack onClickBack={handleGameFilterClick}>
        <Modal.ModalHeader hasBack onClickBack={handleGameFilterClick}>
          {Multilingual(`TOURNAMENT.FILTER`)}
        </Modal.ModalHeader>
        <Modal.ModalBody>
          <ModalFilterDiv>
            <div className="filter-item">
              <h3 className="filter-item--name">{Multilingual(`TOURNAMENT.GAME_LOCATION`)}</h3>
              <div className="filter-item--options">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedLocations.length === 0}
                    onChange={handleGameFilterOption('LOCATION', 'ALL')}
                  />
                  {Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)}
                </label>
                {gameLocations.map((location) => (
                  <label key={location.name}>
                    <input
                      type="checkbox"
                      checked={selectedLocations.includes(location.uniqid)}
                      onChange={handleGameFilterOption('LOCATION', location.uniqid)}
                    />
                    {location.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="filter-item">
              <h3 className="filter-item--name">{Multilingual(`TOURNAMENT.DATE_RANGE`)}</h3>
              <div className="filter-item--options">
                <label>
                  <input
                    name="all"
                    type="checkbox"
                    checked={!selectedDateRange[0] && !selectedDateRange[1]}
                    onChange={handleGameFilterDateRange}
                  />
                  {Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)}
                </label>
                <label>
                  <input
                    name="start"
                    type="date"
                    value={!selectedDateRange[0] ? '' : moment(selectedDateRange[0]).format('YYYY-MM-DD')}
                    onChange={handleGameFilterDateRange}
                  />
                  ~
                  <input
                    name="end"
                    type="date"
                    value={!selectedDateRange[1] ? '' : moment(selectedDateRange[1]).format('YYYY-MM-DD')}
                    onChange={handleGameFilterDateRange}
                  />
                </label>
              </div>
            </div>
          </ModalFilterDiv>
        </Modal.ModalBody>
        <Modal.ModalFooter>
          <Button color="transparent" width="unset" onClick={handleGameFilterClick}>
            {Multilingual(`COMMON.CLOSE`)}
          </Button>
        </Modal.ModalFooter>
      </Modal.Modal>
    </Fragment>
  );
};

export default ScheduleGroup;
